import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Trash2 } from 'lucide-react';
import { LoadingDots } from '../ui/Loading';
import { webhookService } from '../../services/api';
import { generateSessionId } from '../../utils/helpers';
import type { ChatMessage } from '../../types';

interface ChatBriefingProps {
  onComplete: (data: any) => void;

  onBack?: () => void;
  sessionId?: string;
}

// Función para extraer subtemas del resumen
const extractSubtopics = (text: string): string[] => {
  const subtopics: string[] = [];

  // PRIORIDAD 1: Buscar formato "SUBTEMA 1:", "SUBTEMA 2:", etc. (formato del chat)
  const subtemaPattern = /SUBTEMA\s*\d+\s*[:：]\s*([^\n]+)/gi;
  const subtemaMatches = text.matchAll(subtemaPattern);

  for (const match of subtemaMatches) {
    let topic = match[1]?.trim();
    if (topic && topic.length > 10 && topic.length < 200) {
      subtopics.push(topic);
    }
  }

  // Si encontramos subtemas con este formato, devolver directamente
  if (subtopics.length >= 3) {
    return subtopics.slice(0, 4);
  }

  // PRIORIDAD 2: Buscar formato con números "1.", "2.", etc.
  const numberedPattern = /^\s*\d+\.\s+([^\n]+)/gm;
  const numberedMatches = text.matchAll(numberedPattern);

  for (const match of numberedMatches) {
    let topic = match[1]?.trim();
    if (topic && topic.length > 10 && topic.length < 200 &&
      !topic.includes('RESUMEN') &&
      !topic.includes('palabras') &&
      !topic.includes('📋')) {
      if (!subtopics.includes(topic)) {
        subtopics.push(topic);
      }
    }
  }

  // Si encontramos subtemas numerados, devolver
  if (subtopics.length >= 3) {
    return subtopics.slice(0, 4);
  }

  // PRIORIDAD 3: Buscar formato con viñetas "- ", "• ", "* "
  const bulletPattern = /^\s*[-•*]\s+([^\n]+)/gm;
  const bulletMatches = text.matchAll(bulletPattern);

  for (const match of bulletMatches) {
    let topic = match[1]?.trim();
    if (topic && topic.length > 10 && topic.length < 200 &&
      !topic.includes('RESUMEN') &&
      !topic.includes('📋')) {
      if (!subtopics.includes(topic)) {
        subtopics.push(topic);
      }
    }
  }

  // Limitar EXACTAMENTE a los 4 subtemas más relevantes
  return subtopics.slice(0, 4).filter(t => t.length > 10);
};

const STORAGE_KEY = 'chat_briefing_messages';
const SESSION_KEY = 'chat_briefing_session';

export const ChatBriefing: React.FC<ChatBriefingProps> = ({ onComplete, onBack, sessionId: propSessionId }) => {
  // Restaurar mensajes del localStorage
  const loadMessages = (): ChatMessage[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convertir timestamps de string a Date
        return parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (error) {
      // Error loading messages from localStorage
    }

    // Mensaje inicial por defecto
    return [
      {
        role: 'assistant',
        content: '¡Hola! Soy tu asistente para crear contenido. Cuéntame, ¿sobre qué tema quieres escribir?',
        timestamp: new Date(),
      }
    ];
  };

  const [messages, setMessages] = useState<ChatMessage[]>(loadMessages());
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => {
    // Si viene por prop, usarlo
    if (propSessionId) return propSessionId;

    // Recuperar sessionId existente o crear uno nuevo
    const storedSessionId = localStorage.getItem(SESSION_KEY);
    if (storedSessionId) {
      return storedSessionId;
    }
    const newSessionId = generateSessionId();
    localStorage.setItem(SESSION_KEY, newSessionId);
    return newSessionId;
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Guardar mensajes en localStorage cada vez que cambien
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (error) {
      // Error saving messages to localStorage
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Función para limpiar el historial
  const clearHistory = () => {
    if (window.confirm('¿Estás seguro de que quieres borrar toda la conversación?')) {
      const initialMessage = {
        role: 'assistant' as const,
        content: '¡Hola! Soy tu asistente para crear contenido. Cuéntame, ¿sobre qué tema quieres escribir?',
        timestamp: new Date(),
      };
      setMessages([initialMessage]);
      localStorage.removeItem(STORAGE_KEY);
      // Generar nuevo sessionId
      const newSessionId = generateSessionId();
      localStorage.setItem(SESSION_KEY, newSessionId);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await webhookService.sendChatMessage(input.trim(), sessionId);

      // Intentar múltiples formas de extraer el mensaje
      let messageContent = 'Respuesta recibida';

      if (typeof response === 'string') {
        messageContent = response;
      } else if (response.message) {
        messageContent = typeof response.message === 'string' ? response.message : JSON.stringify(response.message, null, 2);
      } else if (response.response) {
        messageContent = typeof response.response === 'string' ? response.response : JSON.stringify(response.response, null, 2);
      } else if (response.output) {
        messageContent = typeof response.output === 'string' ? response.output : JSON.stringify(response.output, null, 2);
      } else if (response.text) {
        messageContent = typeof response.text === 'string' ? response.text : JSON.stringify(response.text, null, 2);
      } else if (response.content) {
        messageContent = typeof response.content === 'string' ? response.content : JSON.stringify(response.content, null, 2);
      } else if (response.data) {
        messageContent = typeof response.data === 'string' ? response.data : JSON.stringify(response.data, null, 2);
      } else {
        // Si no encuentra ningún campo conocido, intenta mostrar todo el objeto
        messageContent = JSON.stringify(response, null, 2);
      }

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: typeof messageContent === 'string' ? messageContent : String(messageContent),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Detectar si es el resumen final
      const isResumen = messageContent.includes('📋 RESUMEN') ||
        messageContent.includes('RESUMEN DE INFORMACIÓN') ||
        messageContent.includes('resumen de información');

      // Si detectamos el resumen, extraer subtemas y pasar a la siguiente fase
      if (isResumen) {
        // Extraer subtemas del resumen
        const subtemas = extractSubtopics(messageContent);

        // Extraer tema principal del resumen
        const temaMatch = messageContent.match(/(?:tema|tópico|asunto|sobre):\s*([^\n]+)/i);
        const temaPrincipal = temaMatch ? temaMatch[1].trim() : 'Tema del contenido';

        setTimeout(() => {
          onComplete({
            rawText: messageContent,
            messages,
            subtopics: subtemas,
            topic: temaPrincipal,
            isResumen: true,
            sessionId // Pass session ID
          });
        }, 1500);
      }
      // Si el briefing está completo (forma antigua)
      else if (response.isComplete || response.briefingComplete) {
        setTimeout(() => {
          onComplete({
            rawText: response.briefingData || response.data || messageContent,
            messages,
            sessionId // Pass session ID
          });
        }, 1000);
      }
    } catch (error) {
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: 'Lo siento, hubo un error al procesar tu mensaje. Por favor, intenta nuevamente.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full max-w-4xl mx-auto w-full">
      <div className="flex-1 overflow-hidden bg-white rounded-2xl shadow-xl border border-purple-100 relative">
        {/* Header decorativo */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />

        {/* Header del Chat */}
        <div className="p-6 border-b border-purple-100 bg-white/50 backdrop-blur-sm relative z-20 mt-1">
          <div className="flex items-center justify-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-md">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Chat Conversacional
              </h2>
              <p className="text-sm text-gray-500 hidden sm:block">
                Tu asistente IA para crear el contenido perfecto
              </p>
            </div>
          </div>
        </div>

        {/* Botón para volver */}
        {onBack && (
          <button
            onClick={onBack}
            className="absolute top-4 left-4 z-10 px-3 py-2 bg-white/80 hover:bg-white text-gray-700 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 shadow-sm border border-purple-100 backdrop-blur-sm"
            title="Volver a selección"
          >
            <span>← Volver</span>
          </button>
        )}

        {/* Botón para limpiar historial */}
        <button
          onClick={clearHistory}
          className="absolute top-4 right-4 z-10 px-3 py-2 bg-red-100/80 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 shadow-sm backdrop-blur-sm"
          title="Limpiar historial de conversación"
        >
          <Trash2 className="w-4 h-4" />
          <span className="hidden sm:inline">Limpiar Historial</span>
        </button>

        <div className="h-[600px] overflow-y-auto px-4 py-6 bg-gradient-to-b from-purple-50/30 to-transparent">
          <div className="max-w-3xl mx-auto space-y-6">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`chat-message flex items-start space-x-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
              >
                {message.role === 'assistant' && (
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center shadow-lg">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[75%] ${message.role === 'user'
                    ? 'bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-3xl rounded-tr-md shadow-md'
                    : 'bg-white text-gray-900 rounded-3xl rounded-tl-md border-2 border-purple-100 shadow-sm'
                    } px-5 py-3.5`}
                >
                  <p className="text-[15px] leading-relaxed whitespace-pre-wrap">
                    {(() => {
                      if (typeof message.content === 'string') {
                        return message.content;
                      }
                      if (message.content === null || message.content === undefined) {
                        return 'Contenido vacío';
                      }
                      try {
                        const jsonString = JSON.stringify(message.content, null, 2);
                        return jsonString;
                      } catch (error) {
                        return '[Contenido no válido]';
                      }
                    })()}
                  </p>
                </div>
                {message.role === 'user' && (
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center shadow-lg">
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="chat-message flex items-start space-x-4 justify-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center shadow-lg animate-pulse">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="bg-white rounded-3xl rounded-tl-md px-5 py-3.5 border-2 border-purple-100 shadow-sm">
                  <LoadingDots text="Pensando" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="border-t border-purple-100 p-5 bg-gradient-to-r from-purple-50/50 via-pink-50/50 to-blue-50/50">
          <div className="max-w-3xl mx-auto flex items-end space-x-3">
            <div className="flex-1 relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Escribe tu mensaje..."
                disabled={isLoading}
                rows={1}
                className="w-full resize-none rounded-2xl border-2 border-purple-200 shadow-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 px-5 py-3.5 pr-14 disabled:bg-gray-100 disabled:cursor-not-allowed text-[15px] min-h-[56px] max-h-32 bg-white/80 backdrop-blur-sm"
                style={{
                  height: 'auto',
                  minHeight: '56px'
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = Math.min(target.scrollHeight, 128) + 'px';
                }}
              />
              <button
                onClick={handleSendMessage}
                disabled={!input.trim() || isLoading}
                className="absolute right-2 bottom-2 w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center justify-center transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Send className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
          <p className="text-xs text-purple-400 text-center mt-3 max-w-3xl mx-auto flex items-center justify-center space-x-2">
            <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse" />
            <span>Presiona Enter para enviar, Shift + Enter para nueva línea</span>
          </p>
        </div>
      </div>
    </div>
  );
};



